import { handleDownloadFile, auth } from '@/shared/lib';
import { useElementChange } from '@/shared/lib/customHooks';
import { useEffect, useRef, useState } from 'react';
import type { SupportModuleDetailsProps } from './supportModuleDetails.types';

/**
 * Drives the support module detail view: login-banner offset for its header and the PDF download.
 * Watches the header element for position changes and re-reads whether the login banner is shown,
 * and exposes a click handler that downloads the module's PDF.
 *
 * @param props - Props carrying the supportModule being displayed.
 * @returns An object carrying hasLoginBanner, the headerRef to attach, and handleDownloadClick.
 */
export const useSupportModuleDetails = ({
  supportModule,
}: Pick<SupportModuleDetailsProps, 'supportModule'>) => {
  const [hasLoginBanner, setHasLoginBanner] = useState(false);

  const headerRef = useRef(null);

  const isChangePosition = useElementChange(headerRef);

  /**
   * Downloads the PDF of the opened support module. Passes supportModule.pdfUrl and a filename built from its title to handleDownloadFile, logging any failure to the console.
   *
   * @returns A promise resolving once the download was triggered.
   */
  const handleDownloadClick = async () => {
    try {
      await handleDownloadFile(supportModule.pdfUrl, `${supportModule.title}.pdf`);
    } catch (error) {
      console.error('Failed to download support module: ', error);
    }
  };

  useEffect(() => {
    if (headerRef.current) {
      setHasLoginBanner(auth.isShowLoginByBanner());
    }
  }, [headerRef.current, isChangePosition]);

  return { hasLoginBanner, headerRef, handleDownloadClick };
};
